// dashboard/patientList/WellnessSection.js
// Wellness group (migration 104). Extracted from App.js (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { c } from '../constants';
import { relDate } from '../utils';

// Deliberately a separate, lighter component from PatientSection/PetCard,
// not a reuse — those are built around conditions.status/alerts,
// dismiss/snooze actions, and trend charts, none of which apply to
// wellness (green/yellow only, no alerts table involvement at all,
// nothing to dismiss). Collapsed by default, positioned below the green
// disease group per the spec's requested dashboard order. Reads only the
// compact wellness_* current-state columns already computed by
// recompute_wellness_status — no full survey history loaded here; a
// clinician wanting the detail can already reach it via the pet's normal
// record, not rebuilt here to keep this section's own scope small.
export default function WellnessSection({ wellnessPatients, error }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // A failed read must render as visibly unavailable, not silently
  // disappear the same way a genuinely empty roster does (Reliability
  // audit, 2026-09-14 — a failed wellness read previously collapsed to
  // the same `[]` as "no wellness patients," and this early return would
  // have hidden even that from a clinician entirely).
  if (!error && !wellnessPatients.length) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0', textAlign: 'left',
        }}
      >
        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c.slate, opacity: 0.4 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: c.slate, textTransform: 'uppercase', letterSpacing: 0.7 }}>
          {t('mainView.sectionWellness')}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: c.textSoft, backgroundColor: c.fog, padding: '1px 8px', borderRadius: 10 }}>
          {wellnessPatients.length}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: c.textSoft }}>{open ? '▲' : '▼'}</span>
      </button>

      {error && (
        <div style={{
          backgroundColor: '#fff5f5', borderRadius: 10, padding: '10px 14px',
          fontSize: 12, color: c.error, border: `1px solid ${c.error}33`, marginBottom: 8,
        }}>
          {t('mainView.wellnessLoadError')}
        </div>
      )}

      {open && (
        <div>
          {wellnessPatients.map(w => (
            <div key={w.pet.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', marginBottom: 8,
              backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                backgroundColor: w.wellnessStatus === 'yellow' ? c.yellow : c.green,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: c.slate }}>
                  {w.pet.name}
                  <span style={{ fontWeight: 400, color: c.textSoft }}> — {w.owner ? `${w.owner.first_name} ${w.owner.last_name}` : ''}</span>
                </div>
                <div style={{ fontSize: 12, color: c.textSoft, marginTop: 2 }}>
                  {w.wellnessSummary || t('mainView.wellnessNoDataYet')}
                </div>
                <div style={{ fontSize: 11, color: c.textSoft, marginTop: 2 }}>
                  {w.lastCheckinAt && t('mainView.wellnessLastCheckin', { date: relDate(w.lastCheckinAt) })}
                  {w.lastCheckinAt && w.nextCheckinAt && ' · '}
                  {w.nextCheckinAt && t('mainView.wellnessNextCheckin', { date: relDate(w.nextCheckinAt) })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
